// CRM Quotations - Redux slice, registered as `state.crmQuotation` (store/rootReducer.js).
// Holds the product templates used to build quotations, the log of quotations already sent,
// and the editable Terms & Conditions text printed on every quotation PDF.
// Server data is loaded by the thunks in ./quotationThunk.js.
import { createSlice } from "@reduxjs/toolkit";
import { getQuotationTemplateThunk, getQuotationLogThunk, addQuotationLogThunk } from "./quotationThunk";

const initialState = {
    // Shared request status for all three thunks: "idle" | "loading" | "succeeded" | "failed".
    status: "idle",
    // Error message from the last rejected thunk, otherwise null.
    error: null,
    // `success` copied from the fulfilled payload (only addQuotationLogThunk returns { success: true }).
    success: "",
    // Product templates: [{ id, item_desc, variables: [{ id, size, amount, gst? }] }] - set by getQuotationTemplateThunk.
    quotationTemplates: [],
    // Sent quotations: [{ id, name, wa_contact_id, quotation_no, quotation_path, created_at }] - set by getQuotationLogThunk.
    quotationLogs: [],
    // Default Terms & Conditions as a small HTML string (<p>, <b>, bullets). Replaced with the user's
    // plain-text version by addTearmAndCondtion (edited in ViewQuotationModal, type "itemDetails").
    tearmAndConditions: `
    <p>The above offer is subject to following terms and conditions:</p>
• <b>Taxes</b>: GST extra 28% on above prices,
• <b>Delivery</b>: Delivery costs extra as actuals,
• <b>Payment</b>: 100% payment advance,
• <b>Warranty</b>: Warranty three years,
• <b>Validity</b>: The offer is valid 15 days
<p>
Please feel free to contact for any clarification or discussion.
We sincerely hope that you shall find our offer best in the industry and we shall soon have the pleasure
of receiving your valuable orders, which will be executed with utmost care.
Thanking you and assuring you our best services and prompt attention all the time.
</p>
`,
};

const crmQuotationSlice = createSlice({
    name: "crmQuotation",
    initialState,
    reducers: {
        // Resets the request flags to idle (currently not dispatched anywhere in the app).
        resetQuotationState: (state, action) => {
            state.status = "idle";
            state.error = null;
            state.success = "";
        },
        // Saves the edited Terms & Conditions text (payload: string) used by the PDF and the HTML preview.
        addTearmAndCondtion: (state, action) => {
            state.tearmAndConditions = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            // getQuotationTemplateThunk -> fills quotationTemplates.
            .addCase(getQuotationTemplateThunk.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(getQuotationTemplateThunk.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.success = action.payload.success;
                state.error = null;
                state.quotationTemplates = action.payload;
            })
            .addCase(getQuotationTemplateThunk.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload || "Quotations failed";
                state.success = "";
            })

            // getQuotationLogThunk -> fills quotationLogs.
            .addCase(getQuotationLogThunk.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(getQuotationLogThunk.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.success = action.payload.success;
                state.error = null;
                state.quotationLogs = action.payload;
            })
            .addCase(getQuotationLogThunk.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload || "Logs failed";
                state.success = "";
            })


            // addQuotationLogThunk -> only updates the flags; the caller re-fetches the logs afterwards.
            .addCase(addQuotationLogThunk.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(addQuotationLogThunk.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.success = action.payload.success;
                state.error = null;
            })
            .addCase(addQuotationLogThunk.rejected, (state, action) => {
                state.status = "failed";
                // Fallback text says "Login failed"; appears to be copied from another slice.
                state.error = action.payload || "Login failed";
                state.success = "";
            })
    },
});
export const { resetQuotationState, addTearmAndCondtion } = crmQuotationSlice.actions;
export default crmQuotationSlice.reducer;